import React, {useState} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useQuery} from "react-query";
import ngage from "services/ngage";
import {Organisation} from "types/Organisation";
import Button from "components/Button";
import {PencilIcon, TrashIcon} from "@heroicons/react/solid";
import Page from "components/Page";
import Card from "components/Card";
import DescriptionList from "components/DescriptionList";
import DescriptionListItem from "components/DescriptionListItem";
import DescriptionTerm from "components/DescriptionTerm";
import DescriptionDetails from "components/DescriptionDetails";
import DeleteOrganisation from '../Delete';
import Link from "components/Link";
import {ExternalLinkIcon, GlobeAltIcon} from '@heroicons/react/outline';
import Chip from "components/Chip";

function ViewOrganisation() {
    const {id} = useParams()
    const {data: organisation, isLoading} = useQuery(
        ['organisation', {id}],
        () => ngage.organisations.get(Number(id))
    )

    const [deletingOrganisation, setDeletingOrganisation] = useState<Organisation | null>(null);
    const navigate = useNavigate()

    // Prepare action handlers
    function handleDeleteOrganisation() {
        if(organisation) setDeletingOrganisation(organisation)
    }

    function handleEditOrganisation() {
        if(id) navigate(`/organisations/${id}/edit`)
    }

    // Prepare page actions
    const actions = <>
        <Button color={Button.colors.WHITE} icon={TrashIcon} onClick={handleDeleteOrganisation}>Delete</Button>
        <Button color={Button.colors.PRIMARY} icon={PencilIcon} onClick={handleEditOrganisation}>Edit</Button>
    </>

    return (
        <Page
            title={organisation?.name || "View Organisation"}
            breadcrumbs={{"Organisations": "/organisations", [organisation?.name || "View Organisation"]: `/organisations/${id}`}}
            actions={actions}
            isLoading={isLoading}
        >
            {organisation &&
                <div className="lg:grid lg:grid-cols-3 lg:gap-4">

                    {/* Logo and background */}
                    <section className="lg:col-span-3 sm:mb-2 h-60 sm:rounded-lg sm:shadow overflow-hidden flex items-center relative bg-brand/20">
                        {/* Background */}
                        {organisation.background &&
                            <img
                                src={organisation.background?.original_url}
                                alt={`${organisation.name} background`}
                                className="z-10 object-cover w-full"
                            />
                        }

                        {/* Logo */}
                        <div className="absolute bottom-5 left-5 h-24 w-24 z-20 bg-white p-1 rounded-lg shadow flex items-center justify-center">
                            {organisation.logo?
                                <img
                                    src={organisation.logo?.preview_url}
                                    alt={`${organisation.name} logo`}
                                    className="z-10 rounded-lg object-scale-down"
                                />
                            :
                                <GlobeAltIcon className="h-20 w-20 text-brand/50" />
                            }
                        </div>
                    </section>


                    {/* Organisation Information */}
                    <section className="lg:col-span-3">
                        <Card heading="Organisation Information">
                            <DescriptionList>
                                <DescriptionListItem span={2}>
                                    <DescriptionTerm>Name</DescriptionTerm>
                                    <DescriptionDetails>
                                        {organisation.name}
                                    </DescriptionDetails>
                                </DescriptionListItem>

                                {!!organisation.about &&
                                    <DescriptionListItem span={2}>
                                        <DescriptionTerm>About</DescriptionTerm>
                                        <DescriptionDetails>
                                            {organisation.about}
                                        </DescriptionDetails>
                                    </DescriptionListItem>
                                }

                                <DescriptionListItem span={1}>
                                    <DescriptionTerm>Email</DescriptionTerm>
                                    <DescriptionDetails>
                                        {organisation.email?
                                            <Link to={`mailto:${organisation.email}`}>{organisation.email}</Link>:
                                            'Not specified'
                                        }
                                    </DescriptionDetails>
                                </DescriptionListItem>

                                <DescriptionListItem span={1}>
                                    <DescriptionTerm>Phone</DescriptionTerm>
                                    <DescriptionDetails>
                                        {organisation.phone?
                                            <Link to={`tel:${organisation.phone}`}>{organisation.phone}</Link>:
                                            'Not specified'
                                        }
                                    </DescriptionDetails>
                                </DescriptionListItem>

                                <DescriptionListItem span={1}>
                                    <DescriptionTerm>Address</DescriptionTerm>
                                    <DescriptionDetails>
                                        {organisation.address?
                                            <address>
                                                {Object.values(organisation.address).filter(line => !!line).map((line, index) => (
                                                    <span key={index} className="not-italic">
                                                    {line}<br/>
                                                </span>
                                                ))}
                                            </address>:
                                            'Not specified'
                                        }
                                    </DescriptionDetails>
                                </DescriptionListItem>

                                {organisation.link &&
                                    <DescriptionListItem span={1}>
                                        <DescriptionTerm>Link</DescriptionTerm>
                                        <DescriptionDetails>
                                            <Chip
                                                name={organisation.link.text}
                                                href={organisation.link.url}
                                                description={organisation.link.url}
                                                icon={ExternalLinkIcon}
                                            />
                                        </DescriptionDetails>
                                    </DescriptionListItem>
                                }
                            </DescriptionList>
                        </Card>
                    </section>

                    {/* Members */}
                    {!!organisation.members.length &&
                        <section className="lg:col-span-3 mt-2">
                            <Card heading="Members" subheading="People that are associated with this organisation">
                                <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {organisation.members.map((member, index) => (
                                        <li key={index}>
                                            <Chip
                                                name={member.name}
                                                href={`/users/${member.id}`}
                                                backgroundSrc={member.profile_photo_url}
                                            />
                                        </li>
                                    ))}
                                </ul>
                            </Card>
                        </section>
                    }
                </div>
            }

            {/* Delete confirmation modal */}
            <DeleteOrganisation
                show={organisation === deletingOrganisation}
                organisation={deletingOrganisation}
                onCancel={() => setDeletingOrganisation(null)}
                onDelete={() => navigate('/organisations')}
                onDeleteFail={() => setDeletingOrganisation(null)}
            />
        </Page>
    );
}

export default ViewOrganisation;